import type { Card } from '@/lib/qcp/protocol';
import type { MisureCarta } from '@/lib/tavolo/carta';

/**
 * Il disegno della carta del Chinese Poker (fase 2, scheda `2026-10-17-tavolo-premium-ofc.md`): le misure e l'ordine della mano.
 *
 * I COLORI (la COPIA da TMA#718 di CriGio @ 8e60f982, `src/components/burraco/Carta.tsx`) stavano qui: con la Carta generica del modulo tavolo (kit-giochi S4) sono in
 * `lib/tavolo/carta.ts`, gli stessi valori, e li usa `components/tavolo/Carta.tsx`. Qui restano le cose del solo CP.
 * Le MISURE sono quelle delle tavole (390×844 in piedi, 844×390 il palco), non quelle del burraco.
 */
export type Seme = Card['s'];

/**
 * Le taglie (px, nel disegno): `mia` (42×58) e `mini` (28×40, l'avversario mentre si gioca) sono le carte sul tavolo IN PIEDI, `avv` (36×50) l'avversario nel palco e le plance del resoconto,
 * `mia-palco` quella del palco orizzontale (50×70), `mano` le carte che si hanno in mano (50×70) e `mano-palco` quelle del palco (56×78).
 * ⚠️ In piedi `mia`, `mini` e `mano` NON sono le misure delle tavole (48×67, 36×50, 62×86): il guscio dell'app (testata d'app + barra) lascia ~600 px e le tavole ne assumevano 844;
 * le misure sono il budget di compattazione del secondo occhio (qchinesepoker-orcb su #719, R1), misurate col banco: le tre file e la mano si vedono insieme. `rango` e `seme` sono i corpi del carattere (px), `dx` il margine.
 */
export type TagliaCp = 'mia' | 'mia-palco' | 'avv' | 'mini' | 'mano' | 'mano-palco';
export const MISURE_CP: Record<TagliaCp, MisureCarta> = {
  mia: { w: 42, h: 58, rango: 15, seme: 12, raggio: 5, dx: 4 },
  'mia-palco': { w: 50, h: 70, rango: 17, seme: 14, raggio: 7, dx: 5 },
  avv: { w: 36, h: 50, rango: 14, seme: 12, raggio: 5, dx: 3 },
  mini: { w: 28, h: 40, rango: 12, seme: 11, raggio: 4, dx: 3 },
  mano: { w: 50, h: 70, rango: 17, seme: 14, raggio: 6, dx: 5 },
  'mano-palco': { w: 56, h: 78, rango: 19, seme: 16, raggio: 7, dx: 5 },
};

/**
 * L'ORDINE della mano («Ordina», I-794). ⚠️ SCELTE DEL PROPRIETARIO DEL CP (qchinesepoker-orcb, 18/10 10:09Z), OPPOSTE a quelle del Burraco (dove l'ordine va in salita e l'asso e il rango piu basso):
 *  - l'asso e ALTO: nel protocollo `r` va da 2 a 14 e l'asso e 14; il server valuta con l'asso alto (la ruota A-2-3-4-5 conta come scala, ma un ordinamento fisso non puo sapere quale ruota si fa);
 *  - per VALORE: dal piu alto al piu basso (A K Q J 10 … 2), come si legge una mano di poker, e a parita per seme; aiuta coppie e tris;
 *  - per SEME: i semi raggruppati e, dentro ogni seme, dal piu alto al piu basso; semi alternati rosso/nero: ♠ ♥ ♣ ♦; aiuta il colore.
 * Il modulo comune ordina in SALITA su due chiavi: qui il rango e negato, cosi «piu alto» viene prima.
 */
export const ORDINE_SEME: Record<Seme, number> = { s: 0, h: 1, c: 2, d: 3 };
export const CHIAVI_ORDINE_CP = { seme: (c: Card) => ORDINE_SEME[c.s], rango: (c: Card) => -c.r };
